import { Component, effect, inject, Input, signal, TemplateRef, viewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { RestApiService } from "src/app/API/restapi";
import { ALERT_TYPE, FILE_TYPE, INJECT_TYPE } from "src/app/constants";
import { DialogItem } from "src/app/dialog-item";
import { ConfirmationDialogComponent } from "src/app/Shared/confirmation-dialog/confirmation-dialog.component";
import { Enums } from "src/app/Shared/enums";
import { SharedService } from "src/app/Shared/shared-service.service";
import { FileInterface } from "src/model/file.interface";
import { FileService } from "src/services/file.service";
import { ImageViewerComponent } from "../image-viewer/image-viewer.component";

@Component({
  selector: "app-thumbnails-list",
  standalone: false,
  templateUrl: "./thumbnails-list.component.html",
  styleUrl: "./thumbnails-list.component.scss",
})
export class ThumbnailsListComponent {
  private activeModal = inject(NgbActiveModal);
  private sharedService = inject(SharedService);
  private fileService = inject(FileService);
  private restApi = inject(RestApiService);
  private enums = inject(Enums);

  @Input() title: string = 'View Files';
  @Input() showDelete: boolean;
  @Input() injectContent: DialogItem[];
  @Input() imgViewerInjectContent: DialogItem[];
  fileArr = signal<FileInterface[]>([]);
  FILE_TYPE = FILE_TYPE;
  INJECT_TYPE = INJECT_TYPE;

  constructor() {
    effect(() => {
      if (this.fileArr().length > 0)
        // must sort to make sure image type file at beginning of array, so that index is accurate in openEnlargeImageModal
        this.fileArr.update((files) => {
          files.sort((a, b) => (b.lastModified > a.lastModified ? 1 : -1));
          files.sort((a, b) => a.fileType - b.fileType);

          return files;
        });
    });
  }

  openEnlargeImageModal(index: number) {
    const modalRef = this.sharedService.openModal(ImageViewerComponent, "largeModal");
    modalRef.componentInstance.fileArr = this.fileArr;
    modalRef.componentInstance.canDelete = this.canDelete;
    modalRef.componentInstance.index.set(index);
    if (this.imgViewerInjectContent?.length > 0) {
      modalRef.componentInstance.injectContent = this.imgViewerInjectContent;
    }
  }

  previewFile(url: string) {
    window.open(this.restApi.apiUrl + url);
  }

  async downloadFile(url: string, filename: string) {
    const d = await this.fileService.getFileByUrl(
      url,
      filename.split("/").pop() || filename,
    );
    this.fileService.downloadFile(d);
  }

  reset() {
    this.activeModal.close();
  }

  get canDelete() {
    if (this.sharedService.isRoles([this.enums.BOSS, this.enums.ADMIN]))
      // Default show delete button
      return this.showDelete !== false;
    else if (this.sharedService.isBranchManager())
      // Default do not show delete button
      return this.showDelete === true;
    return false;
  }

  async confirmDelete(): Promise<boolean> {
    const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
    modalRef.componentInstance.message = `Are you sure you want to delete this file?`;
    return modalRef.result;
  }

  async deleteFile(url: string) {
    const confirm = await this.confirmDelete();
    if (confirm) {
      const deleted = await this.fileService.deleteFile(url);
      if (deleted) {
        this.sharedService.openAlert(
          "Successfully deleted file!",
          ALERT_TYPE.SUCCESS,
        );
        this.fileArr.update((files) => {
          const i = files.findIndex((f) => f.url === url);
          files.splice(i, 1);
          return [...files];
        });
      }
    }
  }
}
